import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { apiGet, apiPut, apiPost, fmtDate } from "@/lib/shieldx";
import { PageHeader, Loader } from "@/components/shieldx/Primitives";
import { Table } from "@/components/shieldx/Table";
import { StatusBadge } from "@/components/shieldx/Badges";
import { PencilBtn, ActionMenu, ActionDialog, EditDialog, Drawer, Timeline, KV } from "@/components/shieldx/SuperAdmin";
import { UsersRound, Eye, Ban, RotateCcw, ShieldX, KeyRound, Unplug } from "lucide-react";

const ROLES = ["super_admin", "org_owner", "security_admin", "security_analyst", "incident_responder", "auditor", "developer", "individual", "viewer"];

export default function SAUsers() {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [action, setAction] = useState(null);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (search) p.set("q", search);
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    setRows(await apiGet(`/super-admin/users?${p.toString()}`));
  }, [search, filters]);
  useEffect(() => { const t = setTimeout(load, 220); return () => clearTimeout(t); }, [load]);

  const open360 = async (u) => {
    setSel(u); setDetail(null); setDetailLoading(true);
    try { setDetail(await apiGet(`/super-admin/users/${u.user_id}/360`)); } finally { setDetailLoading(false); }
  };
  const doAction = (u, act) => setAction({ u, act });
  const runAction = async ({ reason, step_up_token, ...extra }) => {
    const { u, act } = action;
    await apiPost(`/super-admin/users/${u.user_id}/action`, { action: act, reason, step_up_token, ...extra });
    toast.success(`${act} applied to ${u.name}`); load();
  };
  const saveEdit = async (vals) => {
    await apiPut(`/super-admin/users/${edit.user_id}`, { ...vals, reason: "Super Admin field edit" });
    toast.success("User updated"); load();
  };
  if (!rows) return <Loader label="Loading users" />;

  const columns = [
    { key: "name", label: "User", render: (r) => <div><p className="font-medium text-slate-100">{r.name}</p><p className="font-mono text-[11px] text-slate-500">{r.email}</p></div> },
    { key: "org_name", label: "Organization", render: (r) => <span className="text-xs text-slate-400">{r.org_name}</span> },
    { key: "role", label: "Role", render: (r) => <span className="font-mono text-xs text-slate-300">{r.role}</span> },
    { key: "account_status", label: "Status", render: (r) => <StatusBadge value={r.account_status} /> },
    { key: "active_sessions", label: "Sessions", render: (r) => <span className="font-mono tabular-nums text-slate-300">{r.active_sessions}</span> },
    { key: "actions", label: "", render: (r) => (
      <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => open360(r)} data-testid={`user-view360-${r.user_id}`} className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-700/60 bg-slate-800/50 px-2.5 text-xs text-slate-300 hover:border-blue-500/50 hover:text-blue-300"><Eye className="h-3.5 w-3.5" /> 360°</button>
        <PencilBtn onClick={() => setEdit(r)} testid={`user-edit-${r.user_id}`} />
        <ActionMenu testid={`user-menu-${r.user_id}`} items={[
          r.account_status === "SUSPENDED"
            ? { label: "Reactivate", icon: RotateCcw, onClick: () => doAction(r, "REACTIVATE") }
            : { label: "Suspend", icon: Ban, danger: true, onClick: () => doAction(r, "SUSPEND") },
          { label: "Change Role", icon: KeyRound, danger: true, onClick: () => doAction(r, "CHANGE_ROLE") },
          { label: "Revoke Sessions", icon: Unplug, danger: true, onClick: () => doAction(r, "REVOKE_SESSIONS") },
          { label: "Revoke Account", icon: ShieldX, danger: true, onClick: () => doAction(r, "REVOKE") },
        ]} />
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader icon={UsersRound} title="Users & Members" subtitle="Platform-wide user governance. Passwords, keys and secrets are never exposed." />
      <div className="mb-5 flex flex-wrap gap-2.5">
        <input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, id…" className="h-9 min-w-[220px] flex-1 rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-200 outline-none focus:border-blue-500/50" />
        <select data-testid="filter-role" value={filters.role || ""} onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))} className="h-9 rounded-lg border border-slate-800 bg-slate-900/70 px-2.5 text-xs text-slate-300 outline-none">
          <option value="">Role</option>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select data-testid="filter-user-status" value={filters.status || ""} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="h-9 rounded-lg border border-slate-800 bg-slate-900/70 px-2.5 text-xs text-slate-300 outline-none">
          <option value="">Status</option><option value="SUSPENDED">SUSPENDED</option><option value="REVOKED">REVOKED</option>
        </select>
      </div>
      <Table columns={columns} rows={rows} testid="sa-users-table" onRowClick={open360} empty="No users match these filters" />

      <EditDialog open={!!edit} onClose={() => setEdit(null)} title={`Edit ${edit?.name || "user"}`}
        fields={[{ key: "name", label: "Name" }, { key: "job_title", label: "Job Title" }, { key: "phone", label: "Phone" }, { key: "country", label: "Country" }]}
        initial={edit || {}} onSave={saveEdit} />

      <ActionDialog open={!!action} onClose={() => setAction(null)} action={action?.act} targetType="USER" targetId={action?.u?.user_id}
        title={`${action?.act} — ${action?.u?.name || ""}`}
        body={action?.act === "CHANGE_ROLE" ? "Role changes are high-impact and audited." : "This action is recorded in the audit chain."}
        extraFields={action?.act === "CHANGE_ROLE" ? [{ key: "role", label: "New Role", type: "select", options: ROLES.map((r) => ({ value: r, label: r })) }] : []}
        onDone={runAction} />

      <Drawer open={!!sel} onClose={() => { setSel(null); setDetail(null); }} loading={detailLoading}
        title={sel?.name} subtitle={detail?.identity?.email} testid="user-360-drawer"
        badge={detail && <StatusBadge value={detail.security.account_status} />}
        tabs={detail ? [
          { key: "identity", label: "Identity", content: (
            <div className="grid gap-x-8 sm:grid-cols-2">
              <KV label="User ID" value={detail.identity.user_id} mono /><KV label="Email" value={detail.identity.email} mono />
              <KV label="Role" value={detail.identity.role} /><KV label="Organization" value={detail.identity.org_name} />
              <KV label="Job Title" value={detail.identity.job_title} /><KV label="Phone" value={detail.identity.phone} mono />
              <KV label="Auth Provider" value={detail.identity.auth_provider} /><KV label="Created" value={fmtDate(detail.identity.created_at)} />
            </div>
          ) },
          { key: "security", label: "Security", content: (
            <div className="grid gap-x-8 sm:grid-cols-2">
              <div className="py-2"><span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Account Status</span><StatusBadge value={detail.security.account_status} /></div>
              <KV label="Verification" value={detail.security.verification_status} />
              <KV label="MFA" value={detail.security.mfa_status} /><KV label="Passkey" value={detail.security.passkey_status} />
              <KV label="Active Sessions" value={detail.security.active_sessions} /><KV label="Last Login" value={fmtDate(detail.security.last_login)} />
            </div>
          ) },
          { key: "timeline", label: "Timeline", content: <Timeline events={detail.timeline} /> },
        ] : []} />
    </div>
  );
}
